import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../../../../../../store/store';
import { starIcon, successCheckRounded } from './assets';
import { UpperHandFeedbackEnum } from '../../../../../../../../../enums/upper-hand-feedback.enum';
import { submitFeedback } from '../../../../../../../../../store/deals/deals.actions';
import { DetailedAnalysisEmail } from '../../../../../../../../../store/deals/deals.types';
import { useMixPanel } from '../../../../../../../../../components/MixPanel/useMixPanel';

export const UpperHandFeedback: React.FC = (): JSX.Element => {
  const user = useSelector((state: RootState) => state.auth.user);

  const { selectedEmail } = useSelector((state: RootState) => state.deals);

  const { dealId } = useParams<{ dealId: string }>();

  const dispatch = useDispatch();

  const [
    selectedEmailLocal,
    setSelectedEmailLocal,
  ] = useState<DetailedAnalysisEmail | null>(null);

  const [feedbackTooltipOpened, setFeedbackTooltipOpened] = useState<boolean>(
    false
  );

  const [feedbackSubmittedShown, setFeedbackSubmittedShown] = useState<boolean>(
    false
  );

  const { trackEvent } = useMixPanel();

  const { pathname } = useLocation();

  const isInDetailedView =
    pathname.includes('example') && pathname.includes('threads');

  const feedbackInfoUrl =
    'https://help.substrata.me/en/articles/6596810-upper-hand-feedback';

  const toggleFeedbackTooltipOpened: (tooltipOpened?: boolean) => void = (
    tooltipOpened = !feedbackTooltipOpened
  ) => {
    setFeedbackSubmittedShown(false);
    setFeedbackTooltipOpened(tooltipOpened);
  };

  const setUpperHandFeedback: (feedback: UpperHandFeedbackEnum) => void = (
    feedback
  ) => {
    if (selectedEmail?.upperHandFeedback === feedback) {
      return;
    }

    if (user && selectedEmail) {
      dispatch(
        submitFeedback(user.orgId, user.userId, dealId, selectedEmail, feedback)
      );
      trackEvent('upperhand-feedback');
    }

    setTimeout(() => {
      setFeedbackSubmittedShown(true);
    }, 1000);
  };

  useEffect(() => {
    if (!selectedEmail) {
      return;
    }

    if (feedbackSubmittedShown) {
      setTimeout(() => {
        toggleFeedbackTooltipOpened(false);
      }, 500);
    } // eslint-disable-next-line
  }, [feedbackSubmittedShown]);

  useEffect(() => {
    if (selectedEmail) {
      if (selectedEmail.messageId !== selectedEmailLocal?.messageId) {
        toggleFeedbackTooltipOpened(false);
      }

      setSelectedEmailLocal(selectedEmail);
    } // eslint-disable-next-line
  }, [selectedEmail]);

  const attrs = {
    wrapper: {
      className: `${isInDetailedView ? 'disabled' : ''} upper-hand-feedback`,
    },

    icon: {
      wrapper: {
        className: `upper-hand-feedback__icon ${
          feedbackTooltipOpened ? 'active' : ''
        }`,
        onClick: () => toggleFeedbackTooltipOpened(),
      },

      img: { wrapper: { src: starIcon } },
    },

    tooltip: {
      wrapper: { className: 'detailed-view-tooltip__popup open' },
      content: 'Help us make Q better',
    },

    menu: {
      wrapper: {
        className: 'upper-hand-feedback__menu',
      },

      vote: {
        wrapper: {
          className: 'upper-hand-feedback__menu__vote',
        },

        header: {
          wrapper: {
            className: 'upper-hand-feedback__menu__vote__header',
          },

          content: 'Help us make Q better!',

          svg: {
            wrapper: {
              className: 'upper-hand-feedback__menu__vote__header__close',
              onClick: () => toggleFeedbackTooltipOpened(),
            },

            use: {
              wrapper: {
                href: '#close-icon',
              },
            },
          },
        },

        optionCorrect: {
          wrapper: {
            className: `upper-hand-feedback__menu__vote__option ${
              selectedEmail?.upperHandFeedback === UpperHandFeedbackEnum.CORRECT
                ? 'active'
                : ''
            } ${
              selectedEmail?.upperHandFeedback &&
              selectedEmail?.upperHandFeedback !== UpperHandFeedbackEnum.CORRECT
                ? 'disabled'
                : ''
            }`,
            onClick: () => setUpperHandFeedback(UpperHandFeedbackEnum.CORRECT),
          },
          svg: {
            wrapper: {
              className: 'upper-hand-feedback__menu__vote__option__icon',
            },

            use: {
              wrapper: {
                href: '#thumb-up-icon',
              },
            },
          },
          text: {
            wrapper: {
              className: 'upper-hand-feedback__menu__vote__option__text',
            },

            content: 'Upper-Hand seems correct',
          },
        },

        optionIncorrect: {
          wrapper: {
            className: `upper-hand-feedback__menu__vote__option ${
              selectedEmail?.upperHandFeedback ===
              UpperHandFeedbackEnum.INCORRECT
                ? 'active'
                : ''
            } ${
              selectedEmail?.upperHandFeedback &&
              selectedEmail?.upperHandFeedback !==
                UpperHandFeedbackEnum.INCORRECT
                ? 'disabled'
                : ''
            }`,
            onClick: () =>
              setUpperHandFeedback(UpperHandFeedbackEnum.INCORRECT),
          },

          svg: {
            wrapper: {
              className: 'upper-hand-feedback__menu__vote__option__icon',
            },

            use: {
              wrapper: {
                href: '#thumb-down-icon',
              },
            },
          },

          text: {
            wrapper: {
              className: 'upper-hand-feedback__menu__vote__option__text',
            },

            content: 'Upper-Hand seems incorrect',
          },
        },

        optionLink: {
          wrapper: {
            className: `upper-hand-feedback__menu__vote__option link`,
          },

          a: {
            wrapper: {
              className: 'upper-hand-feedback__menu__vote__option__link',
              href: feedbackInfoUrl,
              target: '_blank',
            },

            content: 'Read more',
          },
        },
      },
      submitted: {
        wrapper: {
          className: 'upper-hand-feedback__menu__submitted',
        },

        img: {
          wrapper: {
            className: 'upper-hand-feedback__menu__submitted__img',
            src: successCheckRounded,
          },
        },

        text: {
          wrapper: {
            className: 'upper-hand-feedback__menu__submitted__text',
          },

          content: 'Thank you for your feedback',
        },
      },
    },
  };

  const feedbackMenuVote = (
    <div {...attrs.menu.vote.wrapper}>
      <div {...attrs.menu.vote.header.wrapper}>
        {attrs.menu.vote.header.content}
        <svg {...attrs.menu.vote.header.svg.wrapper}>
          <use {...attrs.menu.vote.header.svg.use.wrapper} />
        </svg>
      </div>
      <div {...attrs.menu.vote.optionCorrect.wrapper}>
        <svg {...attrs.menu.vote.optionCorrect.svg.wrapper}>
          <use {...attrs.menu.vote.optionCorrect.svg.use.wrapper} />
        </svg>
        <div {...attrs.menu.vote.optionCorrect.text.wrapper}>
          {attrs.menu.vote.optionCorrect.text.content}
        </div>
      </div>
      <div {...attrs.menu.vote.optionIncorrect.wrapper}>
        <svg {...attrs.menu.vote.optionIncorrect.svg.wrapper}>
          <use {...attrs.menu.vote.optionIncorrect.svg.use.wrapper} />
        </svg>
        <div {...attrs.menu.vote.optionIncorrect.text.wrapper}>
          {attrs.menu.vote.optionIncorrect.text.content}
        </div>
      </div>
      <div {...attrs.menu.vote.optionLink.wrapper}>
        <a {...attrs.menu.vote.optionLink.a.wrapper}>
          {attrs.menu.vote.optionLink.a.content}
        </a>
      </div>
    </div>
  );

  const feedbackMenuSubmitted = (
    <div {...attrs.menu.submitted.wrapper}>
      <img {...attrs.menu.submitted.img.wrapper} alt="" />
      <div {...attrs.menu.submitted.text.wrapper}>
        {attrs.menu.submitted.text.content}
      </div>
    </div>
  );

  const feedbackMenu = (
    <div {...attrs.menu.wrapper}>
      {feedbackSubmittedShown ? feedbackMenuSubmitted : feedbackMenuVote}
    </div>
  );

  return (
    <div {...attrs.wrapper}>
      <div {...attrs.icon.wrapper}>
        <img {...attrs.icon.img.wrapper} alt="" />
      </div>
      <div {...attrs.tooltip.wrapper}>{attrs.tooltip.content}</div>
      {feedbackTooltipOpened && feedbackMenu}
    </div>
  );
};
